/* eslint-disable camelcase */
import { createReducer } from "../../../utils/redux/create-reducer";
import Types from "./types";
import { reset as resetAppointmentFlow } from "../../../utils/appointment-flow";
import { restoreAppointment } from "../../../utils/parsers/restore-appointmennt-desktop";
import { APPOINTMENT_SCREEN, APPOINTMENT_TYPE } from "../../../constants/app-constants";

const getNotServicibilityStatus = ({ zone, slots, locations }) => {
    let status = false;
    /**
     * check if this case is home inspection only
     */
    if (zone && !locations.length) {
        const locationID = zone.location_id;
        /**
         * already converting date_slot object into an array at the interceptor level
         * hence just need to check on length
         */
        if (slots[locationID] && !slots[locationID].date_slot.length) {
            status = true;
        }
    } else if (!zone && !locations.length) {
        status = true;
    }
    return status;
};

export const INITIAL_STATE = {
    zone: null,
    locations: [],
    slots: null,
    selectedInspectionType: null,
    selectedBranch: null,
    selectedInspectionDate: null,
    selectedInspectionTime: null,
    isAppointmentCreated: false,
    isAppointmentVerified: false,
    appointmentID: null,
    isEditMode: false,
    apiInProgress: false,
    isLoading: true,
    isFetchingSlots: false,
    scrollBottom: false,
    checkedStatus: true,
    isVerified: false,
    prefilledBranch: null,
    prefilledInspectionType: null,
    sellBikeConsent: false,
    isNotServicible: false,
    address: null,
    landmark: null,
    appointmentScreen: APPOINTMENT_SCREEN.DEFAULT,
    appointmentCity: null,
    appointmentPincode: null,
    previousSelectedZone: null,
    homeInspectionPremiumSlotAvailable: false,
    homeInspectionExpressSlotAvailable: false,
    showLocationDrawer: false
};

export const fetchSlotsByLocation = (state = INITIAL_STATE) => {
    return {
        ...state,
        zone: null,
        locations: [],
        slots: null,
        isFetchingSlots: true,
        isNotServicible: false
    };
};

export const fetchSlotsByLocationSuccess = (state = INITIAL_STATE, { zone, locations, slots, homeInspectionPremiumSlotAvailable,
    homeInspectionExpressSlotAvailable }) => {
    let inspectionType = null;
    if (zone === null) {
        inspectionType = APPOINTMENT_TYPE.BRANCH;
    }
    return {
        ...state,
        zone,
        locations,
        slots,
        isFetchingSlots: false,
        isLoading: false,
        isNotServicible: getNotServicibilityStatus({
            zone, locations, slots
        }),
        selectedInspectionType: inspectionType,
        homeInspectionPremiumSlotAvailable,
        homeInspectionExpressSlotAvailable,
        ...(!zone && { appointmentScreen: APPOINTMENT_SCREEN.BOOKING })
    };
};

export const fetchSlotsByLocationFailure = (state = INITIAL_STATE) => {
    return {
        ...state,
        zone: null,
        slots: null,
        locations: [],
        isFetchingSlots: false,
        isLoading: false,
        isNotServicible: false,
        appointmentScreen: APPOINTMENT_SCREEN.BOOKING
    };
};

export const setInspectionType = (state = INITIAL_STATE, { selectedInspectionType }) => {
    return {
        ...state,
        selectedInspectionType,
        ...(selectedInspectionType === "branch" ?
            resetAppointmentFlow("selectedInspectionType") : {}
        )
    };
};

export const setSelectedBranch = (state = INITIAL_STATE, { selectedBranch }) => {
    return { ...state, selectedBranch, ...resetAppointmentFlow("selectedBranch") };
};

export const setInspectionDate = (state = INITIAL_STATE, { selectedInspectionDate }) => {
    return {
        ...state,
        scrollBottom: true,
        selectedInspectionDate,
        ...resetAppointmentFlow("selectedInspectionDate")
    };
};

export const setInspectionTime = (state = INITIAL_STATE, { selectedInspectionTime }) => {
    return {
        ...state,
        scrollBottom: true,
        selectedInspectionTime,
        ...resetAppointmentFlow("selectedInspectionTime")
    };
};

export const resetSelections = (state = INITIAL_STATE) => {
    return {
        ...state,
        selectedBranch: null,
        scrollBottom: false
    };
};

export const submitAppointmentSuccess = (state = INITIAL_STATE, { appointmentID }) => {
    return { ...state, appointmentID };
};

export const submitAppointmentFailure = (state = INITIAL_STATE) => {
    return { ...state, appointmentID: null };
};

export const setEditMode = (state = INITIAL_STATE, { isEditMode }) => {
    return { ...state, isEditMode };
};

export const updateAPIStatus = (state = INITIAL_STATE, { data }) => {
    return { ...state, apiInProgress: data };
};

export const setWhatsappNotificationStatus = (state = INITIAL_STATE) => {
    return { ...state, checkedStatus: !state.checkedStatus };
};

export const restoreAppointmentForm = (state = INITIAL_STATE, { data }) => {
    return restoreAppointment(
        state,
        data
    );
};

export const setPrefilledBranch = (state = INITIAL_STATE, { prefilledBranch }) => {
    return { ...state, prefilledBranch };
};

export const setPrefilledInspectionType = (state = INITIAL_STATE, { prefilledInspectionType }) => {
    return { ...state, prefilledInspectionType };
};

export const setSellBikeConsent = (state = INITIAL_STATE, { sellBikeConsent }) => {
    return { ...state, sellBikeConsent };
};

export const resetLocationData = (state = INITIAL_STATE) => {
    return {
        ...state,
        zone: null,
        slots: null,
        locations: []
    };
};

export const setAddress = (state = INITIAL_STATE, { address }) => {
    return { ...state, address };
};

export const setAddressType = (state = INITIAL_STATE, { addressType }) => {
    return { ...state, addressType };
};

export const setLandmark = (state = INITIAL_STATE, { landmark }) => {
    return { ...state, landmark };
};

export const setAppointmentScreen = (state = INITIAL_STATE, { appointmentScreen }) => {
    return { ...state, appointmentScreen };
};

export const applySelectionsAndData = (state = INITIAL_STATE, { data }) => {
    const {
        locations,
        slots,
        zone,
        inspectionType,
        branch,
        date,
        time
    } = data;
    return {
        ...state,
        locations,
        slots,
        zone,
        selectedInspectionType: inspectionType,
        selectedBranch: branch,
        selectedInspectionDate: date,
        selectedInspectionTime: time,
        isNotServicible: getNotServicibilityStatus({
            zone, locations, slots
        })
    };
};

export const setPreviousSelectedZonne = (state = INITIAL_STATE, { zone }) => {
    return { ...state, previousSelectedZone: zone };
};

export const setAppointmentId = (state = INITIAL_STATE, { appointmentId }) => {
    return { ...state, appointmentID: appointmentId };
};

export const setIsHiAvailable = (state = INITIAL_STATE, { isHiAvailable }) => {
    return { ...state, isHiAvailable};
};

export const setInspectionLocation = (state = INITIAL_STATE, { inspectionLocation }) => {
    return {...state, inspectionLocation };
};

export const setLocationName = (state = INITIAL_STATE, { locationName }) => {
    return { ...state, locationName };
};

export const setIsLocationSearchEnabled = (state = INITIAL_STATE, { isLocationSearchEnabled }) => {
    return { ...state, isLocationSearchEnabled};
};
export const setLocationCoords = (state = INITIAL_STATE, { locCoords }) => {
    return { ...state, locCoords, isLocationConfirmed: false};
};

export const setLocation = (state = INITIAL_STATE, { location }) => {
    return { ...state, location};
};
export const toggleLocationDrawer = (state = INITIAL_STATE, {status}) => {
    return { ...state, showLocationDrawer: status};
};

export const HANDLERS = {
    [Types.FETCH_SLOTS_BY_LOCATIONS]: fetchSlotsByLocation,
    [Types.FETCH_SLOTS_BY_LOCATIONS_SUCCESS]: fetchSlotsByLocationSuccess,
    [Types.FETCH_SLOTS_BY_LOCATIONS_FAILURE]: fetchSlotsByLocationFailure,
    [Types.SET_INSPECTION_TYPE]: setInspectionType,
    [Types.SET_SELECTED_BRANCH]: setSelectedBranch,
    [Types.SET_INSPECTION_DATE]: setInspectionDate,
    [Types.SET_INSPECTION_TIME]: setInspectionTime,
    [Types.RESET_SELECTIONS]: resetSelections,
    [Types.SUBMIT_APPOINTMENT_SUCCESS]: submitAppointmentSuccess,
    [Types.SUBMIT_APPOINTMENT_FAILURE]: submitAppointmentFailure,
    [Types.SET_EDIT_MODE]: setEditMode,
    [Types.UPDATE_API_STATUS]: updateAPIStatus,
    [Types.SET_WHATSAPP_NOTIFICATION_STATUS]: setWhatsappNotificationStatus,
    [Types.RESTORE_APPOINTMENT_FORM]: restoreAppointmentForm,
    [Types.SET_PREFILLED_BRANCH]: setPrefilledBranch,
    [Types.SET_PREFIIED_INSPECTION_TYPE]: setPrefilledInspectionType,
    [Types.SET_SELL_BIKE_CONSENT]: setSellBikeConsent,
    [Types.RESET_LOCATION_DATA]: resetLocationData,
    [Types.SET_ADDRESS]: setAddress,
    [Types.SET_LANDMARK]: setLandmark,
    [Types.SET_APPOINTMENT_SCREEN]: setAppointmentScreen,
    [Types.APPLY_SELECTIONS_AND_DATA]: applySelectionsAndData,
    [Types.SET_APPOINTMENT_ID]: setAppointmentId,
    [Types.SET_PREVIOUS_SELECTED_ZONE]: setPreviousSelectedZonne,
    [Types.SET_ADDRESS_TYPE]: setAddressType,
    [Types.SET_IS_HI_AVAILABLE]: setIsHiAvailable,
    [Types.SET_INSPECTION_LOCATION]: setInspectionLocation,
    [Types.SET_CURRENT_LOCATION_NAME]: setLocationName,
    [Types.SET_IS_LOCATION_SEARCH_ENABLED]: setIsLocationSearchEnabled,
    [Types.SET_LOCATION_COORDS]: setLocationCoords,
    [Types.SET_LOCATION]: setLocation,
    [Types.TOGGLE_LOCATION_DRAWER]: toggleLocationDrawer

};

export default createReducer(INITIAL_STATE, HANDLERS);
